<template>
	<div class="d-flex justify-content-center align-items-start vh-100 pt-3">
		<b-card no-body class="custom-card">
			<b-card-body style="padding: 28px 16px;">
				<b-card-body class="p-0">
					<div class="d-flex justify-content-center align-items-center p-4">
						<img 
							v-if="bannerUrl" 
							:src="bannerUrl" 
							class="me-1"
							height="60"
						>
						<img src="/Logo.ico" height="68">
					</div>
				</b-card-body>
				<b-card-text style="padding: 0px 21px 21px;">
					<b-container>
						<b-row>
							<b-col cols="12">
								<label for="username" class="form-label">Username</label>
							</b-col>
							<b-col cols="12">
								<b-form-input v-model="username" placeholder="Username" class="form-control"
									:state="usernameValid" required />
								<b-form-invalid-feedback>
									Username is Required.
								</b-form-invalid-feedback>
							</b-col>
						</b-row>
						<br />
						<b-row>
							<b-col cols="12">
								<label for="password" class="form-label">Password</label>
							</b-col>
							<b-col cols="12">
								<b-input-group>
									<template #append>
										<span 
											class="input-group-text text-secondary"
											style="padding-inline: 7px;"
											@click="isVisiblePassword = !isVisiblePassword"
										>
											<Icon :icon="isVisiblePassword ? 'bx-hide' : 'bx-show'" style="height: 1.2em; width: 1.2em;" />
										</span>
									</template>
									<b-form-input v-model="password" :type="isVisiblePassword ? 'text' : 'password'" placeholder="********"
										class="form-control" :state="passwordValid" @keyup.enter="validateLoginForm" required />
									<b-form-invalid-feedback>
										Password is Required.
									</b-form-invalid-feedback>
								</b-input-group>
							</b-col>
						</b-row>
						<b-row>
							<b-col cols="6" class="px-0 ">
								<b-form-checkbox value="remember" unchecked-value="not_accepted">
									Remember me
								</b-form-checkbox>
							</b-col>
							<b-col cols="6" class="px-0 d-flex justify-content-end align-items-center">
								<a href="#" @click="showDialogForgot(true)">
									Forgot Password?
								</a>
							</b-col>
						</b-row>
						<b-row class="mt-4">
							<b-col cols="12">
								<b-button variant="primary" class="d-block w-100"
									@click="validateLoginForm">Login</b-button>
							</b-col>
						</b-row>
					</b-container>
				</b-card-text>
			</b-card-body>
		</b-card>
	</div>

	<b-modal v-model="dialogForgot" centered size="lg" variant="primary" title="Forgot Password">
		<b-container>
			<b-row>
				<b-col cols="12" sm="6" md="12">
					<b-form-input v-model="forgotUsername" size="lg" placeholder="Username" class="form-control"
						:state="forgotUsernameValid" :disabled="isSendingMail" required />
					<b-form-invalid-feedback>
						Username is Required.
					</b-form-invalid-feedback>
				</b-col>
			</b-row>
		</b-container>
		<template #footer>
			<div class="d-flex w-100 justify-content-end">
				<b-button variant="light" size="sm" class="float-right" :disabled="isSendingMail"
					@click="showDialogForgot(false)">
					Close
				</b-button> &nbsp;
				<b-button variant="link" @click="validateForgotForm">
					<b-spinner v-if="isSendingMail" small />
					{{ isSendingMail ? '' : 'Submit' }}
				</b-button>
			</div>
		</template>
	</b-modal>

	<b-modal v-model="dialogResend" centered size="lg" variant="primary" title="Email has been sent">
		<b-card-text>
			<b-container>
				<div class="d-flex w-100 justify-content-center">
					<label class="text-primary">Please check your email and click the received link to reset a
						password.</label>
				</div>
				<div class="d-flex w-100 justify-content-center">
					<img :src="imgSendMail" alt="Check Circle" style="width: 100px; height: 100px; ">
				</div>
			</b-container>
		</b-card-text>
		<template #footer>
			<div class="d-flex w-100 justify-content-center">
				<label class="text-primary mt-2">Don't receive email? </label> &nbsp;
				<b-button variant="link" @click="resendEmail"><b-spinner v-if="isSendingMail" small />{{ isSendingMail ?
					'' : 'Resend' }}</b-button>
			</div>
		</template>
	</b-modal>
</template>

<script setup>
import { ref, onMounted } from "vue"
import { useRouter } from 'vue-router'
import dataControlApi from "@/api/userApi"
import { useAuthStore, useUsersStore } from "@/stores"
import { useToast } from 'vue-toast-notification'
import imgSendMail from "@/assets/send-email.png"
import { getDeviceType } from "@/helpers/utility"
import { getBanner } from "@/helpers/bannerHelper"

const authStore = useAuthStore()
const usersStore = useUsersStore()
const toast = useToast()

const router = useRouter()

const deviceType = getDeviceType()

const username = ref('')
const password = ref('')
const forgotUsername = ref('')
const bannerUrl = ref(null)

const usernameValid = ref(null)
const passwordValid = ref(null)
const forgotUsernameValid = ref(null)
const isVisiblePassword = ref(false)

const dialogForgot = ref(false)
const dialogResend = ref(false)
const isSendingMail = ref(false)

const proxyServerUrl = process.env.VUE_APP_PROXY_SERVER

onMounted(async () => {
  bannerUrl.value = await getBanner()
})

async function authenticate() {
	var result = await authStore.login(username.value?.trim().toLowerCase(), password.value?.trim())

	if (result == true) {
		await usersStore.clearDict() // refresh user dict

		if (deviceType === 'mobile' && usersStore.pageConfigs.length > 0) {
			return router.push(`/ticket/${usersStore.pageConfigs[0].id}`)
		} else {
			return router.push("/dashboard")
		}
	} else {
		toast.open({
			message: `<strong>Notification</strong><br> ${result}`,
			type: "error",
			duration: 5000,
			position: "bottom-right",
		})
	}
}

async function submitForget() {
	isSendingMail.value = true
	try {
		const response = await new dataControlApi().sendResetEmail(forgotUsername.value)
		if (response.status == 200) {
			toast.open({
				message: `<strong>Send reset password success</strong>`,
				type: "success",
				duration: 5000,
				position: "bottom-right",
			})

			showDialogForgot(false)
			dialogResend.value = true
		}
		else {
			toast.open({
				message: `<strong>Send reset password fail</strong><br>`,
				type: "error",
				duration: 5000,
				position: "bottom-right",
			})
		}
	} catch (e) {
		console.error(e)
		toast.open({
			message: `<strong>Send reset password fail</strong>`,
			type: "error",
			duration: 5000,
			position: "bottom-right",
		})
	}
	isSendingMail.value = false
}

async function resendEmail() {
	isSendingMail.value = true
	try {
		const response = await sendResetEmail()

		if (response.status == 200) {
			toast.open({
				message: `<strong>${response.data}</strong>`,
				type: "success",
				duration: 5000,
				position: "bottom-right",
			})
		}

	} catch (e) {
		console.error(e)
	}
	isSendingMail.value = false
}

async function sendResetEmail() {
	return await new dataControlApi().sendResetEmail(forgotUsername.value)
}

function validateLoginForm() {
	usernameValid.value = username.value.trim() !== ''
	passwordValid.value = password.value.trim() !== ''

	if (usernameValid.value && passwordValid.value) {
		authenticate()
	} else {
		if (usernameValid.value === '') {
			usernameValid.value = false
		}

		if (passwordValid.value === '') {
			passwordValid.value = false
		}
	}
}

function validateForgotForm() {
	if (forgotUsername.value === '') {
		forgotUsernameValid.value = false
		return
	}

	submitForget()
}

function showDialogForgot(isShow) {
	dialogForgot.value = isShow

	if (isShow) {
		forgotUsername.value = ''
	}
}
</script>

<style scoped>
.custom-card {
	width: 90%;
	/* Default width on small screens */
	max-width: 440px;
	/* Set a max-width for larger screens */
}

:deep(.col-12) {
	padding: 0px !important
}
</style>